import {
  faArrowsSpin,
  faArrowUpRightFromSquare,
  faArrowUpShortWide,
  faBarcode,
  faBarsStaggered,
  faBoxArchive,
  faChevronDown,
  faChevronUp,
  faClock,
  faExclamationTriangle, faFax,
  faFile,
  faObjectGroup,
  faPrint,
  faQrcode,
  faShareFromSquare,
  faTruck,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _last from 'lodash/last';
import _round from 'lodash/round';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import ConvertedUnits from 'src/components/ConvertedUnits';
import ExpandableCard from 'src/components/ExpandableCard';
import Feature from 'src/components/feature';
import PrinterTypeMark from 'src/components/icons/PrinterTypeMark';
import IntlDate from 'src/components/intl-date';
import MaintenanceHealthBadge from 'src/components/maintenance/MaintenanceHealthBadge';
import { MaterialsLayout } from 'src/components/MaterialsLayout';
import { ToolingStockCardPreview } from 'src/components/ToolingStockCard';
import ToolingStockCustomFields from 'src/components/ToolingStockCustomFields';
import FeaturesContext from 'src/context/FeaturesContext';
import useDocuments from 'src/hooks/services/useDocuments';
import {
  ALLOWED_RUN_STATUSES_FOR_TOOLING_STOCK_SCAN,
  API_RESOURCES,
  BUREAU_BARCODE_FORMAT,
  LOCALSTORAGE_KEYS,
  MATERIAL_BATCH_ACTIONS,
  MATERIAL_UNITS,
  PRIORITY_BADGE_BORDER_COLORS,
  PRIORITY_BADGE_COLORS,
  PRIORITY_BADGE_TEXT_COLORS,
  RUN_ACTIONS,
  RUN_STATUS,
  RUN_STATUS_LABELS,
  STATUS_BADGE_BORDER_COLORS,
  STATUS_BADGE_COLORS,
  STATUS_BADGE_TEXT_COLORS,
} from 'src/utils/constants';
import { FEATURES, isFeatureEnabled } from 'src/utils/features';
import { convertMassToOtherUnit } from 'src/utils/math';
import { getMachineTypeFromUrl } from 'src/utils/mlineUtils';
import { getPriorityLabel } from 'src/utils/stringUtils';
import { getRouteURI, getShortUuid, getUuid } from 'src/utils/url';
import useLocalstorage from 'src/utils/useLocalStorage';

import routes from '../../../../utils/routes';
import RunDocuments from './_run-documents';
import RunHeaderCompactBar from './_run-header-compact-bar';
import RunMenu from './_run-menu';
import RunUserStatus from './_run-user-status';
import SharedCustomFields from './_shared-custom-fields';

export const STATUS_LABELS_OVERRIDES = {
  ...RUN_STATUS_LABELS,
  [RUN_STATUS.inProgress]: 'In Progress',
};

export const getRunStatusBadgeStyles = (run) => ({
  backgroundColor: STATUS_BADGE_COLORS[run.status],
  border: `1px solid ${STATUS_BADGE_BORDER_COLORS[run.status]}`,
  color: STATUS_BADGE_TEXT_COLORS[run.status],
});

const calculateMaterialMass = (volume, density, massUnit) => {
  // Calculate mass of material in g
  const mass = volume * density;

  // Convert mass to input unit
  return _round(convertMassToOtherUnit(mass, MATERIAL_UNITS.GRAM, massUnit), 3);
};

const RunHeader = ({
  attendTimeEntryUser,
  run,
  workstation,
  scheduledRun,
  runMachineTimeEntries,
  latestAttendTimeTrackingEntry,
  workstationNextRun,
  materialInfo,
  labelsByPiece,
  isToolingStockTypeFeatureEnabled,
  toolingStockData,
  accessInfo,
  publicGroupsByUri,
  setGroupsState,
  onRunUpdate,
  onRefreshRunAndWorkstationData,
  checklist,
  postProcessors,
  printers,
  buildData,
  runDocuments,
  runPrinterType,
  runMaterial,
  runMaterialInfo,
  isMaterialManagementEnabled,
  currentGroupIndex,
  totalGroups,
  isEquipmentMaintenanceEnabled,
}) => {
  const { documentList, userList } = useDocuments(run.run_documents);
  const { features } = useContext(FeaturesContext);
  const toolingSliderRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  const machineName = getMachineTypeFromUrl(workstation?.uri);
  const isPrinter = machineName === API_RESOURCES.PRINTER;
  const isPostProcessor = machineName === API_RESOURCES.POST_PROCESSOR;
  const isShipping = machineName === API_RESOURCES.SHIPPING;

  const estMaterialWeight = (runMaterial && runMaterial.materials) && runMaterialInfo ?
    calculateMaterialMass(runMaterial.materials[0].quantity_estimate, runMaterialInfo.density, runMaterialInfo.units)
    : null;

  const isMaintenanceEnabled = isFeatureEnabled(features, FEATURES.EQUIPMENT_MAINTENANCE);
  const isItarControlFeatureEnabled = isFeatureEnabled(features, FEATURES.ITAR_CONTROL_BY_PIECE);
  const isMaterialManagementFeatureEnabled = isFeatureEnabled(features, FEATURES.MATERIAL_MANAGEMENT);
  const estimatedStartDate =
    scheduledRun
    && scheduledRun.estimates
    && scheduledRun.estimates.start
    && new Date(scheduledRun.estimates.start);
  const estimatedEndDate =
    scheduledRun
    && scheduledRun.estimates
    && scheduledRun.estimates.end
    && new Date(scheduledRun.estimates.end);

  const showPauseReason = Boolean(
    latestAttendTimeTrackingEntry && latestAttendTimeTrackingEntry.end_time && latestAttendTimeTrackingEntry.notes &&
    run.status !== RUN_STATUS.complete,
  );

  const navigate = useNavigate();

  const isPaused = run.status === RUN_STATUS.paused;
  const isComplete = run.status === RUN_STATUS.complete;
  const latestMachineTimeEntry = _last(runMachineTimeEntries);
  const hasStarted =
    run.status === RUN_STATUS.inProgress
    // If run is not ended - we need to check for the latest time entry too
    && (!latestMachineTimeEntry || !latestMachineTimeEntry.end_time);
  const displayEstimates = !hasStarted && !isPaused && !isComplete && estimatedStartDate;

  const showNextWorkstationRun = run.status === RUN_STATUS.complete;
  const showRunUserStatus = isPaused || hasStarted;

  const [scanMode] = useLocalstorage(LOCALSTORAGE_KEYS.SCAN_MODE, BUREAU_BARCODE_FORMAT.QR);
  const isBarcode = scanMode === BUREAU_BARCODE_FORMAT.BARCODE;

  const toolsList = Array.isArray(toolingStockData)
    ? toolingStockData
    : toolingStockData && typeof toolingStockData === 'object' && toolingStockData?.toolingStock
      ? [toolingStockData]
      : [];

  useEffect(() => {
    toolingSliderRef.current?.innerSlider?.onWindowResized?.();
  }, [expanded, toolsList.length]);

  const isAppropriateStatusForToolingStock = ALLOWED_RUN_STATUSES_FOR_TOOLING_STOCK_SCAN.includes(run.status);

  const printerPageRoute = workstation?.uri ?
    getRouteURI(routes.printerMaterial, { uuid: getUuid(workstation.uri) })
    : null;

  const handleScanToolingStock = () => {
    navigate(getRouteURI(routes.scan,
      {},
      {
        runUri: run.uri,
        action: RUN_ACTIONS.SCAN_TOOLING_STOCK,
        entity: API_RESOURCES.TOOLING_STOCK,
      }));
  };

  const handleBatchScan = () => {
    navigate(getRouteURI(routes.scan,
      {},
      {
        printer: workstation.uri,
        entity: API_RESOURCES.MATERIAL_BATCH,
        action: MATERIAL_BATCH_ACTIONS.MACHINE_LOAD,
        actionFromPrinter: true,
      }
    ));
  };

  const renderHelpButton = (handleOnClick, icon, text) => {
    return (
      <button
        type="button"
        className="run-workstation-scan-button"
        onClick={handleOnClick}
      >
        <FontAwesomeIcon icon={icon} className="run-workstation-scan-button__icon" />
        <span>{text}</span>
      </button>
    );
  };

  const renderSnapshotCustomFields = (tool) => {
    const snapshotMetadata = tool?.toolLink?.snapshot_metadata;
    if (!snapshotMetadata) {
      return (
        <div className='tooling-stock-card-new__custom-fields'>
          Snapshot not available for this tool link
        </div>
      );
    }

    const customFieldValues = Array.isArray(snapshotMetadata.custom_field_values)
      ? snapshotMetadata.custom_field_values
      : [];

    return (
      <ToolingStockCustomFields
        customFieldValues={customFieldValues}
        emptyMessage='No captured custom fields'
      />
    );
  };

  const renderSnapshotHeaderTimestamp = (tool) => {
    const snapshotTimestamp = tool?.toolLink?.created
      || tool?.toolLink?.created_at
      || tool?.toolLink?.updated_at
      || tool?.toolLink?.updated
      || tool?.toolLink?.timestamp;
    const parsedSnapshotDate = snapshotTimestamp ? new Date(snapshotTimestamp) : null;
    const hasValidSnapshotDate = parsedSnapshotDate && !Number.isNaN(parsedSnapshotDate.getTime());

    return (
      <>
        <span>Snapshot as of: </span>
        {hasValidSnapshotDate
          ? <IntlDate suppressHydrationWarning withTime disableSeconds date={parsedSnapshotDate} />
          : '—'}
      </>
    );
  };

  const renderToolingStockPreview = () => {
    const handleOverlayToggle = () => {
      requestAnimationFrame(() => {
        toolingSliderRef.current?.innerSlider?.onWindowResized?.();
      });
    };

    const scanButtonIcon = isBarcode ? faBarcode : faQrcode;

    const scanButton = isAppropriateStatusForToolingStock ? renderHelpButton(handleScanToolingStock, scanButtonIcon, 'Scan Tool') : null;

    const toolHeaderText = isAppropriateStatusForToolingStock ? "ACTIVE TOOL" : "INACTIVE TOOL"
    if (toolsList.length === 0) {
      return (
        <div className='tooling-stock-card-new'>
          <div className='tooling-stock-card-new__header'>{toolHeaderText}</div>
          {scanButton}
        </div>
      );
    }

    if (toolsList.length === 1) {
      const tool = toolsList[0];
      return (
        <div className='tooling-stock-card-new'>
          <div className='tooling-stock-card-new__header'>{toolHeaderText}</div>
          <ToolingStockCardPreview
            expanded={expanded}
            location={tool.toolingStockLocation}
            subLocation={tool.toolingStockSubLocation}
            toolingType={tool.toolingType}
            toolingStock={tool.toolingStock}
            setExpanded={setExpanded}
            customFieldContent={renderSnapshotCustomFields(tool)}
            customFieldHeaderContent={renderSnapshotHeaderTimestamp(tool)}
            isMaintenanceEnabled={isEquipmentMaintenanceEnabled}
            onOverlayToggle={handleOverlayToggle}
          />
          {scanButton}
        </div>
      );
    }

    const getDotsClass = (expanded) =>
      `slick-dots${expanded ? '' : ' slick-dots-not-expanded'}`;

    const handleBeforeChange = () => {
      // Ensure no element inside the current slide retains focus when slide becomes hidden
      if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };

    const handleAfterChange = () => {
      toolingSliderRef.current?.innerSlider?.onWindowResized?.();
    };

    return (
      <div className='tooling-stock-card-new tooling-stock-card-new--carousel'>
        <div className='tooling-stock-card-new__header'>{toolHeaderText}S</div>
        <Slider
          ref={toolingSliderRef}
          swipe
          dots
          adaptiveHeight
          arrows={false}
          initialSlide={0}
          infinite={false}
          speed={200}
          className='tooling-stock-carousel'
          dotsClass={getDotsClass(expanded)}
          customPaging={() => <button type="button" className="slick-dot-button" />}
          beforeChange={handleBeforeChange}
          afterChange={handleAfterChange}
        >
          {toolsList.map((tool, idx) => (
            <div key={tool.toolLink?.uri || tool.toolingStock?.uri || `tool-${idx}`} className='tooling-stock-carousel__slide'>
              <ToolingStockCardPreview
                expanded={expanded}
                location={tool.toolingStockLocation}
                subLocation={tool.toolingStockSubLocation}
                toolingType={tool.toolingType}
                toolingStock={tool.toolingStock}
                setExpanded={setExpanded}
                customFieldContent={renderSnapshotCustomFields(tool)}
                customFieldHeaderContent={renderSnapshotHeaderTimestamp(tool)}
                onOverlayToggle={handleOverlayToggle}
              />
            </div>
          ))}
        </Slider>
        {scanButton}
      </div>
    );
  };



  const renderMaterialInfo = () => {
    const { materialBatch, material } = materialInfo;

    if (materialBatch && material && Object.keys(materialBatch).length && Object.keys(material).length) {
      const { uuid: currentBatchId, usage_cycles: cycles, quantity, units } = materialBatch;
      const { name } = material;

      const batchID = getShortUuid(currentBatchId);

      const badges = [
        {
          icon: faBoxArchive,
          text: batchID,
          tooltip: 'Batch ID',
        },
        {
          icon: faObjectGroup,
          text: (
            <>
              {name}
              <span className="run-workstation-materials__color-dot" />
            </>
          ),
          tooltip: 'Material Type',
          className: 'run-workstation-materials__badge--material',
        },
        {
          icon: faArrowUpShortWide,
          text: <ConvertedUnits quantity={quantity} units={units} />,
          tooltip: 'Amount Loaded',
        },
        {
          icon: faArrowsSpin,
          text: cycles,
          tooltip: 'Batch Usage Cycles',
        },
      ];

      return <MaterialsLayout loading={loading} setLoading={setLoading} badges={badges} />;
    }

    return null;
  };

  const renderRunPrinterType = () => {
    return (
      <OverlayTrigger placement="top" overlay={<Tooltip id="printer-type-tooltip">Printer Type</Tooltip>}>
        <span className="run-workstation-body__printer-type-badge">
          <PrinterTypeMark />
          <span className="run-workstation-body__printer-type-name">{runPrinterType?.name}</span>
        </span>
      </OverlayTrigger>
    )
  };

  const renderEmptyBatchMessage = () => {
    const loadBatchButtonIcon = isBarcode ? faBarcode : faQrcode

    const loadBatchButton = renderHelpButton(handleBatchScan, loadBatchButtonIcon, 'Load Batch')

    return (
      <div className='mt30'>
        There is no batch currently loaded into the machine
        {loadBatchButton}
      </div>
    )
  };

  const renderPrinterInfo = () => {
    const handleRedirectToPrinterPage = () => navigate(printerPageRoute)

    const scanButton = renderHelpButton(
      handleRedirectToPrinterPage,
      faArrowUpRightFromSquare,
      'View Printer Details',
    );

    const printerType = renderRunPrinterType()

    return (
      <div className='tooling-stock-card-new'>
        <div className='tooling-stock-card-new__header'>WORKSTATION DETAILS</div>
        <div className='tooling-stock-card-new__printer-row'>
          {printerType}
        </div>
        {printerPageRoute && scanButton}
      </div>
    )
  };

  const labelsByPieceCount = Object.keys(labelsByPiece).length;
  const [isExpanded, setIsExpanded] = useState(false);

  // Show the compact bar once the natural cards have scrolled fully out of
  // the viewport. The bar is a separate, fixed-position element - cards
  // never become sticky, the document height never changes, and nothing
  // duplicates on screen: the full cards are visible at the top of the
  // page, the compact bar is visible only after the user has scrolled past.
  const [compactBarVisible, setCompactBarVisible] = useState(false);
  const cardsBottomSentinelRef = useRef(null);

  useEffect(() => {
    const sentinel = cardsBottomSentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setCompactBarVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const runPriority = getPriorityLabel(run.priority);

  const isMaterialEmpty = !materialInfo ||
    (!materialInfo.materialBatch && Object.keys(materialInfo?.material || {}).length === 0);

  const workstationHasContent =
    (
      !isToolingStockTypeFeatureEnabled &&
      isMaterialEmpty &&
      isPrinter &&
      isMaterialManagementFeatureEnabled
    ) ||
    isToolingStockTypeFeatureEnabled ||
    !!materialInfo ||
    !!runPrinterType;

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const runStatusBadgeStyles = getRunStatusBadgeStyles(run);

  const runPriorityBadgeStyles = run.priority != null ? {
    backgroundColor: PRIORITY_BADGE_COLORS[run.priority],
    border: `1px solid ${PRIORITY_BADGE_BORDER_COLORS[run.priority]}`,
    color: PRIORITY_BADGE_TEXT_COLORS[run.priority],
  } : null;

  const runStatusBadgeLabel = STATUS_LABELS_OVERRIDES[run.status];

  return (
    <>
      <div className='run-header-card card-base'>
        <div className={`run-header-card__header ${isExpanded ? 'run-header-card__header--expanded' : ''}`} onClick={toggleExpanded}>
          <div className="run-header-card__left card-left">
            <div className={`run-header-card__share-icon icon-container ${isExpanded ? 'run-header-card__share-icon--expanded' : ''}`}>
              <FontAwesomeIcon icon={faShareFromSquare} />
            </div>
            <div className="run-workstation-body__info info-section">
              <div className={`run-header-card__title ${isExpanded ? 'run-header-card__title--expanded' : ''}`}>
                <div className='run-header-card__title-content'>
                  {run.customer_id || run.name}
                  <Feature featureName={FEATURES.USER_RUN_TIME_TRACKING}>
                    {showRunUserStatus && (
                      <RunUserStatus
                        runStatus={run.status}
                        runUser={attendTimeEntryUser?.name}
                        latestAttendingTimeEntry={latestAttendTimeTrackingEntry}
                      />
                    )}
                  </Feature>
                </div>
                <div className="run-header-card__badges">
                  <span
                    className="run-header-card__badge"
                    style={runStatusBadgeStyles}
                  >
                    {runStatusBadgeLabel}
                  </span>
                  <span className="run-header-card__badge run-header-card__badge--info">
                    <FontAwesomeIcon icon={faFile} className="run-header-card__badge-icon" />
                    {run.run_documents ? run.run_documents.length : 0}
                  </span>
                </div>
                <Feature featureName={FEATURES.USER_RUN_TIME_TRACKING}>
                  {showPauseReason && (
                    <div className="run-header-card__pause-reason">
                      <strong>Reason for Pause:</strong>{' '}
                      {latestAttendTimeTrackingEntry.notes}
                    </div>
                  )}
                </Feature>
              </div>
              {!isExpanded && (
                <div className="run-workstation-body__subtitle subtitle-text">Tap to view details</div>
              )}
              {isExpanded && (
                <div className="run-header-card__header-content">
                  {displayEstimates && (
                    <div className="run-header-card__time">
                      <FontAwesomeIcon icon={faClock} className="run-header-card__time-icon" />
                      <div className="run-header-card__time-text">
                        <IntlDate suppressHydrationWarning withTime disableSeconds date={estimatedStartDate} />
                        {estimatedEndDate && (
                          <>
                            {' → '}
                            <IntlDate suppressHydrationWarning withTime disableSeconds date={estimatedEndDate} />
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="run-header-card__badges">
                    {runPriority && runPriorityBadgeStyles && (
                      <OverlayTrigger
                        placement="bottom"
                        overlay={<Tooltip id="priority-tooltip">Priority</Tooltip>}
                      >
                        <div
                          className="run-header-card__badge"
                          style={runPriorityBadgeStyles}
                        >
                          {runPriority}
                        </div>
                      </OverlayTrigger>
                    )}
                    {!!labelsByPieceCount && (
                      <div>
                        <OverlayTrigger
                          placement='bottom'
                          overlay={
                            isItarControlFeatureEnabled ? (
                              <Tooltip>
                                {labelsByPieceCount} piece(s) in this run are ITAR controlled. Please use necessary caution.
                              </Tooltip>
                            ) : (
                              <Tooltip>
                                {labelsByPieceCount} piece(s) in this run are export controlled. Please use necessary caution.
                              </Tooltip>
                            )
                          }
                        >
                          <div className="run-header-card__warning-badge">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="run-header-card__warning-icon" />
                            Warning
                          </div>
                        </OverlayTrigger>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="run-header-card__right">
            <div className="run-header-card__right-actions">
              <FontAwesomeIcon
                icon={isExpanded ? faChevronUp : faChevronDown}
                className="run-header-card__chevron"
              />
              <div className="run-header-card__menu-wrapper" onClick={(e) => e.stopPropagation()}>
                <RunMenu
                  run={run}
                  checklist={checklist}
                  accessInfo={accessInfo}
                  publicGroupsByUri={publicGroupsByUri}
                  setGroupsState={setGroupsState}
                  postProcessors={postProcessors}
                  printers={printers}
                  onRunUpdate={onRunUpdate}
                  onRefreshRunAndWorkstationData={onRefreshRunAndWorkstationData}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="expandable-card-body-wrapper" data-open={isExpanded}>
          <div className="expandable-card-body-inner">
            <div className="run-header-card__body">
              <div className="run-header-card__section">
                <div className="run-header-card__section-title">Additional Documents</div>
                <div className="run-header-card__documents">
                  <RunDocuments documents={documentList} users={userList} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SharedCustomFields
        buildData={buildData}
        estMaterialWeight={estMaterialWeight}
        estMaterialWeightUnits={runMaterialInfo?.units}
        additionalDocuments={runDocuments}
      />

      {workstation && (
        <ExpandableCard
          defaultExpanded
          className='run-workstation-body card-base'
          isCollapsible={workstationHasContent}
          renderHeader={({ isExpanded: isWorkstationExpanded, onToggle: toggleWorkstationExpanded }) => (
            <div className="run-workstation-body__header" onClick={toggleWorkstationExpanded}>
              <div className={`run-workstation-body__icon icon-container ${isWorkstationExpanded ? 'run-workstation-body__icon--expanded' : ''}`}>
                <FontAwesomeIcon icon={isPostProcessor ? faFax : isShipping ? faTruck : faPrint} />
              </div>
              <div className="run-workstation-body__info info-section gap-5">
                <div className={`run-workstation-body__title fs-16 ${isWorkstationExpanded ? 'run-workstation-body__title--expanded' : ''}`}>
                  <div className='d-flex flex-column gap-5'>
                    {isPostProcessor ? (
                      <Link
                        to={getRouteURI(routes.postProcessorQueue, { uuid: getUuid(workstation.uri) })}
                        className="run-workstation-body__link"
                      >
                        {workstation.name}
                      </Link>
                    ) : isShipping ? (
                      <span className="run-workstation-body__title-name">
                        {workstation.name}
                      </span>
                    ) : (
                      <Link
                        to={getRouteURI(routes.printerMaterial, { uuid: getUuid(workstation.uri) })}
                        className="run-workstation-body__link"
                      >
                        {workstation.name}
                      </Link>
                    )}
                    {isMaintenanceEnabled && (
                      <MaintenanceHealthBadge
                        className="align-self-start"
                        linkClassName="align-self-start"
                        equipmentUri={workstation.uri}
                        maintenanceRoute={isPostProcessor
                          ? routes.postProcessorMaintenance
                          : routes.printerMaintenance}
                      />
                    )}
                  </div>
                </div>
                {!isWorkstationExpanded && (
                  <div className="run-workstation-body__subtitle subtitle-text">Tap to view details</div>
                )}
              </div>
              <div className="run-workstation-body__right">
                <div className='d-flex align-items-center gap-10'>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip">
                        Number of Runs in Progress or Worked Ready / Number of Runs in Queue
                      </Tooltip>
                    }
                  >
                    <div className="run-workstation-body__progress">
                      <FontAwesomeIcon icon={faBarsStaggered} className="run-workstation-body__progress-icon" />
                      <span>{workstation.in_progress?.length || 0} of {workstation.in_progress_max || 0}</span>
                    </div>
                  </OverlayTrigger>
                  {workstationHasContent && (
                    <FontAwesomeIcon
                      icon={isWorkstationExpanded ? faChevronUp : faChevronDown}
                      className="run-workstation-body__chevron"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        >
          {workstationHasContent && (
            <div className="run-workstation-body__content">
              {isPrinter && renderPrinterInfo()}
              {
                isMaterialEmpty
                && isPrinter
                && isMaterialManagementFeatureEnabled
                && renderEmptyBatchMessage()
              }
              {isToolingStockTypeFeatureEnabled && renderToolingStockPreview()}
              {isMaterialManagementEnabled && materialInfo && renderMaterialInfo()}
            </div>
          )}
        </ExpandableCard>
      )}

      {/* Sentinel placed right after the 3 cards. Goes out of view above when
          the user has scrolled past — that's our trigger for the compact bar. */}
      <div ref={cardsBottomSentinelRef} aria-hidden='true' className='run-header-cards-end' />

      <RunHeaderCompactBar
        visible={compactBarVisible}
        run={run}
        workstation={workstation}
        buildData={buildData}
        additionalDocuments={runDocuments}
        estMaterialWeight={estMaterialWeight}
        estMaterialWeightUnits={runMaterialInfo?.units}
        runPrinterType={runPrinterType}
        currentGroupIndex={currentGroupIndex}
        totalGroups={totalGroups}
      />

      {showNextWorkstationRun && (
        <h2 className='fs-mobile-20'>
          Next at Workstation:
          {' '}
          {
            workstationNextRun
              ? (
                <Link to={getRouteURI(routes.run, { uuid: getUuid(workstationNextRun.uri) })}>
                  {workstationNextRun.name}
                </Link>
              )
              : 'None'
          }
        </h2>
      )}
    </>
  );
};

RunHeader.propTypes = {
  attendTimeEntryUser: PropTypes.shape({
    name: PropTypes.string,
  }),
  run: PropTypes.shape({
    uri: PropTypes.string.isRequired,
    // eslint-disable-next-line camelcase
    pieces_locked: PropTypes.bool.isRequired,
    name: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    scheduled: PropTypes.shape({
      start: PropTypes.string,
      end: PropTypes.string,
    }).isRequired,
    // eslint-disable-next-line camelcase
    customer_id: PropTypes.string.isRequired,
    prints: PropTypes.arrayOf(PropTypes.string).isRequired,
    // eslint-disable-next-line camelcase
    run_documents: PropTypes.arrayOf(PropTypes.string).isRequired,
    priority: PropTypes.number.isRequired,
  }),
  runMachineTimeEntries: PropTypes.arrayOf(PropTypes.shape({
    // eslint-disable-next-line camelcase
    start_time: PropTypes.string.isRequired,
    // eslint-disable-next-line camelcase
    end_time: PropTypes.string,
  })),
  scheduledRun: PropTypes.shape({
    estimates: PropTypes.shape({
      start: PropTypes.string,
      end: PropTypes.string,
    }),
  }),
  workstation: PropTypes.shape({
    name: PropTypes.string.isRequired,
    // eslint-disable-next-line camelcase
    in_progress: PropTypes.arrayOf(PropTypes.string).isRequired,
    // eslint-disable-next-line camelcase
    in_progress_max: PropTypes.number.isRequired,
    uri: PropTypes.string.isRequired,
  }),
  latestAttendTimeTrackingEntry: PropTypes.shape({
    // eslint-disable-next-line camelcase
    start_time: PropTypes.string,
    // eslint-disable-next-line camelcase
    end_time: PropTypes.string,
    notes: PropTypes.string,
  }),
  workstationNextRun: PropTypes.shape({
    uri: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  materialInfo: PropTypes.shape({
    materialBatch: PropTypes.object,
    material: PropTypes.object,
  }),
  labelsByPiece: PropTypes.shape({}).isRequired,
  isToolingStockTypeFeatureEnabled: PropTypes.bool.isRequired,
  toolingStockData: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({
      toolingStock: PropTypes.shape({}),
      toolingType: PropTypes.shape({}),
      toolingStockLocation: PropTypes.string,
      toolingStockSubLocation: PropTypes.string,
      toolLink: PropTypes.shape({
        active: PropTypes.bool,
        created: PropTypes.string,
        'created_at': PropTypes.string,
        updated: PropTypes.string,
        'updated_at': PropTypes.string,
        timestamp: PropTypes.string,
        'snapshot_metadata': PropTypes.shape({
          'custom_field_values': PropTypes.arrayOf(PropTypes.shape({
            'custom_field': PropTypes.string,
            'field_id': PropTypes.string,
            'field_name': PropTypes.string,
            value: PropTypes.oneOfType([
              PropTypes.string,
              PropTypes.number,
              PropTypes.bool,
              PropTypes.arrayOf(PropTypes.any),
              PropTypes.shape({}),
            ]),
          })),
        }),
      }),
    })),
    PropTypes.shape({
      toolingStock: PropTypes.shape({}),
      toolingType: PropTypes.shape({}),
      toolingStockLocation: PropTypes.string,
      toolingStockSubLocation: PropTypes.string,
      toolLink: PropTypes.shape({
        active: PropTypes.bool,
        created: PropTypes.string,
        'created_at': PropTypes.string,
        updated: PropTypes.string,
        'updated_at': PropTypes.string,
        timestamp: PropTypes.string,
        'snapshot_metadata': PropTypes.shape({
          'custom_field_values': PropTypes.arrayOf(PropTypes.shape({
            'custom_field': PropTypes.string,
            'field_id': PropTypes.string,
            'field_name': PropTypes.string,
            value: PropTypes.oneOfType([
              PropTypes.string,
              PropTypes.number,
              PropTypes.bool,
              PropTypes.arrayOf(PropTypes.any),
              PropTypes.shape({}),
            ]),
          })),
        }),
      }),
    }),
  ]),
  accessInfo: PropTypes.shape({
    // eslint-disable-next-line camelcase
    target_uri: PropTypes.string,
    actions: PropTypes.arrayOf(PropTypes.shape({
      allowed: PropTypes.bool.isRequired,
      type: PropTypes.string.isRequired,
    })),
  }),
  publicGroupsByUri: PropTypes.objectOf(PropTypes.shape({})),
  setGroupsState: PropTypes.func,
  onRunUpdate: PropTypes.func,
  onRefreshRunAndWorkstationData: PropTypes.func,
  checklist: PropTypes.instanceOf(Object),
  postProcessors: PropTypes.arrayOf(PropTypes.shape({
    uri: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })),
  printers: PropTypes.arrayOf(PropTypes.shape({
    uri: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })),
  buildData: PropTypes.shape({
    customFields: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string,
      })
    ),
    additive: PropTypes.shape({
      // eslint-disable-next-line camelcase
      layer_thickness: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    estimatesAdditive: PropTypes.shape({
      materials: PropTypes.shape({
        base: PropTypes.string.isRequired,
      }),
    }),
  }),
  runPrinterType: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }),
  runMaterial: PropTypes.shape({
    run: PropTypes.string.isRequired,
    materials: PropTypes.arrayOf(
      PropTypes.shape({
        material: PropTypes.string.isRequired,
        // eslint-disable-next-line camelcase
        quantity_actual: PropTypes.number,
        // eslint-disable-next-line camelcase
        quantity_estimate: PropTypes.number.isRequired,
        type: PropTypes.string.isRequired,
        uri: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  runMaterialInfo: PropTypes.shape({
    density: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
    units: PropTypes.string.isRequired,
    uri: PropTypes.string.isRequired,
  }).isRequired,
  isMaterialManagementEnabled: PropTypes.bool.isRequired,
  runDocuments: PropTypes.shape({}).isRequired,
  currentGroupIndex: PropTypes.number,
  totalGroups: PropTypes.number,
  isEquipmentMaintenanceEnabled: PropTypes.bool,
};

RunHeader.defaultProps = {
  currentGroupIndex: 0,
  totalGroups: 0,
  attendTimeEntryUser: null,
  run: {},
  latestAttendTimeTrackingEntry: null,
  runMachineTimeEntries: [],
  scheduledRun: null,
  workstation: null,
  workstationNextRun: null,
  materialInfo: null,
  accessInfo: null,
  publicGroupsByUri: {},
  setGroupsState: null,
  onRunUpdate: null,
  onRefreshRunAndWorkstationData: null,
  checklist: {},
  postProcessors: [],
  printers: [],
};

export default RunHeader;
